import { observer } from 'mobx-react'
import React, { useState } from 'react'
import ProgressContainer from '../progress/Progress'
import { Dropzone } from '../'
import {
	FormControl,
	makeStyles,
	createStyles,
	Theme,
	FormHelperText,
	TextField
} from '@material-ui/core'
import MenuItem from '@material-ui/core/MenuItem'
import { useIntl } from 'react-intl'

type networkType = {
	postFileToServer: (
		companyId: number,
		formData: unknown,
		fileCategory: string,
		onUploadProgress: (progressEvent: unknown) => void,
		userId: number,
		orderId?: number
	) => Promise<any>

	deleteFile: (fileID: number) => Promise<any>
}

interface UploadProps {
	companyId: number
	userId: number
	value: File[]
	addFile: (file: File) => void
	removeFile: (file: File) => void
	network: networkType
	orderId?: number
	submitError?: boolean
	useFileCategorySelector?: boolean
}

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		formControl: {
			margin: theme.spacing(1),
			minWidth: 120
		},
		selectEmpty: {
			marginTop: theme.spacing(2)
		}
	})
)

const FILE_CATEGORIES = [
	{ value: 'CV', label: 'CV' },
	{ value: 'InternationalReport', label: 'Internationell bakgrundskontroll' },
	{ value: 'Personalia', label: 'Personalia' },
	{ value: 'Economi', label: 'Ekonomi' },
	{ value: 'CorporateEngagements', label: 'Bolagskartläggning' },
	{ value: 'Legal', label: 'Juridik' },
	{ value: 'MediaVerification', label: 'Mediakontroll' },
	{ value: 'BKOfPartner', label: 'Bakgrundskontroll av partner' },
	{ value: 'Other', label: 'Övrig' }
]

const Upload: React.FC<UploadProps> = observer(
	({
		value: fileIDs,
		addFile,
		removeFile,
		submitError,
		useFileCategorySelector,
	}: UploadProps) => {
		const [state, setState] = useState({
			fileIDs: fileIDs,
			files: [],
			uploading: false,
			uploadProgress: {},
			successfullUploaded: false,
			mapper: {},
			fileCategory: 'CV',
			error: submitError
		})

		const intl = useIntl()
		const maximumFileSizeInBytes = 5 * 1024 * 1024

		const onFilesAdded = files => {
			files = files.filter(file => {
				if (state.files.some(stateFile => stateFile.name === file.name)) {
					return false
				}
				return true
			})

			setState(prevState => ({
				...prevState,
				files: [...prevState.files, ...files]
			}))

			files.forEach(file => {
				if (!file.type.includes("pdf") || file.size > maximumFileSizeInBytes) {
					failed(file.name)
				} else {
					done(file.name)
					addFile(file)
				}
			})
		}

		const deleteFile = async fileName => {
			//Remove file from UI

			const fileToDelete = state.files.find(file => file.name === fileName)
			const updatedFiles = state.files.filter(x => x.name !== fileName)

			setState(prevState => ({
				...prevState,
				files: updatedFiles,
			}))

			//delete the fileID from parent component
			removeFile(fileToDelete)
		}

		const done = filename => {
			const copy = state.uploadProgress
			copy[filename] = { state: 'done', percentage: 100 }
			setState(prevState => ({ ...prevState, uploadProgress: copy }))
		}

		const failed = filename => {
			const copy = state.uploadProgress
			copy[filename] = { state: 'error', percentage: 100 }
			setState(prevState => ({ ...prevState, uploadProgress: copy }))
		}

		const onUploadProgress = (progressEvent: unknown) => {
			// Do whatever you want with the native progress event
			// For some reason, this method needs to exist. We tried removing it and stuff broke. Feel free to investigate if you'd like.
		}

		const renderProgress = filename => {
			if (state.uploadProgress[filename]) {
				return (
					<ProgressContainer
						deleteFile={deleteFile}
						filename={filename}
						onUploadProgress={onUploadProgress}
						successfullUploaded={state.successfullUploaded}
						uploading={state.uploading}
						uploadProgress={state.uploadProgress[filename]}
					/>
				)
			}
		}

		const classes = useStyles()
		return (
			<div className={submitError ? 'Upload--hasError' : 'Upload'}>
				<div className="Content">
					{useFileCategorySelector && (
						<FormControl variant="outlined" className={classes.formControl}>
							<TextField
								select
								margin="none"
								variant="outlined"
								value={state.fileCategory}
								label={intl.formatMessage({ id: 'common.category' })}
								onChange={(event: React.ChangeEvent<{ value: unknown }>) =>
									setState(prevState => ({
										...prevState,
										fileCategory: event.target.value as string
									}))
								}
								inputProps={{
									name: 'fileCategory',
									id: 'fileCategory-helper'
								}}>
								{FILE_CATEGORIES.map(category => (
									<MenuItem key={category.value} value={category.value}>
										{category.label}
									</MenuItem>
								))}
							</TextField>
							<FormHelperText>
								{intl.formatMessage({ id: 'common.categoryHelpText' })}
							</FormHelperText>
						</FormControl>
					)}

					<Dropzone
						files={state.files}
						onFilesAdded={onFilesAdded}
						submitError={submitError}
					/>

					<div className="Files">
						{state.files.map(file => {
							return (
								<div key={file.name} className="Row">
									<span className="Filename">{file.name}</span>
									{renderProgress(file.name)}
								</div>
							)
						})}
					</div>
				</div>
			</div>
		)
	}
)
export default Upload
